import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import apiService from "../../services/api";
import { formatCurrency } from "../../utils/currencyUtils";

const s = {
  card: {
    background: "white", borderRadius: "0.5rem", border: "1px solid #e2e8f0",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)", padding: "1rem", marginTop: "1rem",
  },
  heading: { fontSize: "1rem", fontWeight: "600", color: "#001272", marginBottom: "0.25rem" },
  subtext: { fontSize: "0.8rem", color: "#6b7280", marginBottom: "0.75rem" },
  amount: { fontSize: "0.875rem", fontWeight: "600", textAlign: "right", whiteSpace: "nowrap" },
  deleteBtn: {
    background: "none", border: "1px solid #fca5a5", color: "#dc2626",
    borderRadius: "0.25rem", padding: "0.2rem 0.5rem", fontSize: "0.75rem", cursor: "pointer",
  },
  editBtn: {
    background: "none", border: "1px solid #d1d5db", color: "#374151",
    borderRadius: "0.25rem", padding: "0.2rem 0.5rem", fontSize: "0.75rem", cursor: "pointer",
  },
  input: {
    width: "100%", padding: "0.4rem 0.6rem", border: "1px solid #d1d5db",
    borderRadius: "0.375rem", fontSize: "0.875rem", boxSizing: "border-box",
  },
  addBtn: {
    padding: "0.4rem 0.9rem", fontSize: "0.8rem", fontWeight: "500",
    background: "#001272", color: "white", border: "none", borderRadius: "0.25rem", cursor: "pointer",
  },
  error: { fontSize: "0.8rem", color: "#dc2626", marginTop: "0.25rem" },
  empty: { fontSize: "0.85rem", color: "#9ca3af", fontStyle: "italic", margin: "0.5rem 0" },
};

const PendingAdjustmentsPanel = ({ yearMonth, isLocked }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ canonicalTutorId: "", amount: "", description: "" });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ amount: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const { data: items = [] } = useQuery({
    queryKey: ["pendingAdjustments", yearMonth],
    queryFn: () => apiService.getPendingAdjustments(yearMonth),
    staleTime: 30 * 1000,
  });

  const { data: tutorsPage } = useQuery({
    queryKey: ["allTutors"],
    queryFn: () => apiService.getTutors(0, 500),
    staleTime: 5 * 60 * 1000,
  });
  const tutors = (tutorsPage?.content ?? [])
    .slice()
    .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["pendingAdjustments", yearMonth] });

  const handleAdd = async () => {
    const amt = parseFloat(form.amount);
    if (!form.canonicalTutorId) { setError("Select a tutor."); return; }
    if (isNaN(amt)) { setError("Enter a valid amount."); return; }
    if (!form.description.trim()) { setError("Description is required."); return; }
    setSaving(true); setError(null);
    try {
      await apiService.createPendingAdjustment(yearMonth, form.canonicalTutorId, amt, form.description.trim());
      setForm({ canonicalTutorId: "", amount: "", description: "" });
      invalidate();
    } catch (e) {
      setError(e.message || "Failed to add.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this pending adjustment?")) return;
    setSaving(true);
    try {
      await apiService.deletePendingAdjustment(yearMonth, id);
      invalidate();
    } catch (e) {
      setError(e.message || "Failed to delete.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setEditForm({ amount: item.amount.toString(), description: item.description });
    setError(null);
  };

  const saveEdit = async () => {
    const amt = parseFloat(editForm.amount);
    if (isNaN(amt)) { setError("Enter a valid amount."); return; }
    if (!editForm.description.trim()) { setError("Description is required."); return; }
    setSaving(true); setError(null);
    try {
      await apiService.updatePendingAdjustment(yearMonth, editId, amt, editForm.description.trim());
      setEditId(null);
      invalidate();
    } catch (e) {
      setError(e.message || "Failed to update.");
    } finally {
      setSaving(false);
    }
  };

  const renderItem = (item) => {
    const isEditing = editId === item.id;

    if (isMobile) {
      return (
        <div key={item.id} style={{ padding: "0.6rem 0", borderBottom: "1px solid #f1f5f9" }}>
          {isEditing ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              <div style={{ fontSize: "0.8rem", fontWeight: "600", color: "#001272" }}>{item.tutorName}</div>
              <input
                style={s.input}
                type="number"
                step="0.01"
                value={editForm.amount}
                onChange={(e) => setEditForm((f) => ({ ...f, amount: e.target.value }))}
                placeholder="Amount"
              />
              <input
                style={s.input}
                value={editForm.description}
                onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Description"
              />
              <div style={{ display: "flex", gap: "0.4rem" }}>
                <button style={s.addBtn} onClick={saveEdit} disabled={saving}>Save</button>
                <button style={s.editBtn} onClick={() => setEditId(null)}>Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "0.5rem" }}>
                <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#001272" }}>{item.tutorName}</span>
                <span style={{ ...s.amount, color: item.amount >= 0 ? "#166534" : "#dc2626" }}>
                  {formatCurrency(item.amount)}
                </span>
              </div>
              <div style={{ fontSize: "0.8rem", color: "#374151", marginTop: "0.2rem" }}>{item.description}</div>
              {!isLocked && (
                <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.4rem" }}>
                  <button style={s.editBtn} onClick={() => startEdit(item)}>Edit</button>
                  <button style={s.deleteBtn} onClick={() => handleDelete(item.id)} disabled={saving}>Delete</button>
                </div>
              )}
            </>
          )}
        </div>
      );
    }

    // Desktop row
    return (
      <div key={item.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.5rem 0", borderBottom: "1px solid #f1f5f9" }}>
        <span style={{ fontSize: "0.875rem", fontWeight: "500", color: "#001272", minWidth: "140px" }}>{item.tutorName}</span>
        {isEditing ? (
          <>
            <input
              style={{ ...s.input, flex: "1" }}
              type="number"
              step="0.01"
              value={editForm.amount}
              onChange={(e) => setEditForm((f) => ({ ...f, amount: e.target.value }))}
              placeholder="Amount"
            />
            <input
              style={{ ...s.input, flex: "2" }}
              value={editForm.description}
              onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Description"
            />
            <button style={s.addBtn} onClick={saveEdit} disabled={saving}>Save</button>
            <button style={s.editBtn} onClick={() => setEditId(null)}>Cancel</button>
          </>
        ) : (
          <>
            <span style={{ fontSize: "0.875rem", color: "#374151", flex: 1 }}>{item.description}</span>
            <span style={{ ...s.amount, minWidth: "70px", color: item.amount >= 0 ? "#166534" : "#dc2626" }}>
              {formatCurrency(item.amount)}
            </span>
            {!isLocked && (
              <>
                <button style={s.editBtn} onClick={() => startEdit(item)}>Edit</button>
                <button style={s.deleteBtn} onClick={() => handleDelete(item.id)} disabled={saving}>Delete</button>
              </>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div style={s.card}>
      <h3 style={s.heading}>Pre-Generation Adjustments</h3>
      <p style={s.subtext}>
        These adjustments are automatically included each time payroll is generated for {yearMonth}.
        They persist across regenerations.
      </p>

      {items.length === 0 ? (
        <p style={s.empty}>No pre-generation adjustments yet.</p>
      ) : (
        items.map(renderItem)
      )}

      {!isLocked && (
        <div style={{ marginTop: "0.75rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {isMobile ? (
            <>
              <select
                style={{ ...s.input, background: "white" }}
                value={form.canonicalTutorId}
                onChange={(e) => { setForm((f) => ({ ...f, canonicalTutorId: e.target.value })); setError(null); }}
              >
                <option value="">— Select tutor —</option>
                {tutors.map((t) => (
                  <option key={t.canonicalTutorId} value={t.canonicalTutorId}>
                    {[t.firstName, t.lastName].filter(Boolean).join(" ")}
                  </option>
                ))}
              </select>
              <input
                style={s.input}
                type="number"
                step="0.01"
                placeholder="Amount (e.g. 10 or -5)"
                value={form.amount}
                onChange={(e) => { setForm((f) => ({ ...f, amount: e.target.value })); setError(null); }}
              />
              <input
                style={s.input}
                type="text"
                placeholder="Description"
                value={form.description}
                onChange={(e) => { setForm((f) => ({ ...f, description: e.target.value })); setError(null); }}
              />
              <button style={{ ...s.addBtn, opacity: saving ? 0.7 : 1 }} onClick={handleAdd} disabled={saving}>
                {saving ? "Adding…" : "Add Adjustment"}
              </button>
            </>
          ) : (
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              <select
                style={{ flex: "2", minWidth: "140px", padding: "0.4rem 0.6rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.875rem", background: "white" }}
                value={form.canonicalTutorId}
                onChange={(e) => { setForm((f) => ({ ...f, canonicalTutorId: e.target.value })); setError(null); }}
              >
                <option value="">— Select tutor —</option>
                {tutors.map((t) => (
                  <option key={t.canonicalTutorId} value={t.canonicalTutorId}>
                    {[t.firstName, t.lastName].filter(Boolean).join(" ")}
                  </option>
                ))}
              </select>
              <input
                style={{ flex: "1", minWidth: "80px", padding: "0.4rem 0.6rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.875rem" }}
                type="number"
                step="0.01"
                placeholder="Amount (e.g. 10 or -5)"
                value={form.amount}
                onChange={(e) => { setForm((f) => ({ ...f, amount: e.target.value })); setError(null); }}
              />
              <input
                style={{ flex: "3", minWidth: "160px", padding: "0.4rem 0.6rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.875rem" }}
                type="text"
                placeholder="Description"
                value={form.description}
                onChange={(e) => { setForm((f) => ({ ...f, description: e.target.value })); setError(null); }}
              />
              <button style={{ ...s.addBtn, opacity: saving ? 0.7 : 1 }} onClick={handleAdd} disabled={saving}>
                {saving ? "Adding…" : "Add"}
              </button>
            </div>
          )}
          {error && <p style={s.error}>{error}</p>}
        </div>
      )}
    </div>
  );
};

export default PendingAdjustmentsPanel;
